/**
 * Item Classification State Types
 *
 * Classification State is the fourth orthogonal state machine on an Item.
 * It answers: "Has this item's category and altitude been determined, and by whom?"
 *
 * States: empty → proposed → approved
 *
 * - empty    — no category or altitude assigned yet
 * - proposed — an agent (ATC or any future agent) has suggested values,
 *              awaiting human confirmation
 * - approved — a human has confirmed the classification (either by accepting
 *              a proposal or by entering values directly)
 *
 * Orthogonal invariants:
 * - Classification state is independent of exit_status (active/deferred/archived).
 * - Classification state is independent of readiness — an item can be `approved`
 *   for classification but still fail other readiness checks (Ring constraints,
 *   required attribute values, etc.).
 * - `approved` does not mean the category/altitude cannot change; a human can
 *   always override. Each override creates a new lineage record.
 *
 * See: DOCS_V4/00-canon/03-state-model-and-transitions.md §7
 */

import type { AltitudeLevel } from './itemLifecycle';
import type { ItemLanguageType } from './lifecycle';
import type { AltitudeQuestionResponse } from '../../lib/altitudeQuestions';

// ── Status ────────────────────────────────────────────────────────────────────

/** The four canonical classification states. */
export type ClassificationStatus = 'empty' | 'proposed' | 'approved';

/** Who initiated the classification proposal. */
export type ClassificationProposedBy = 'agent' | 'user';

// ── Classification fields ─────────────────────────────────────────────────────

/**
 * How the work is expected to be carried out as AI becomes an organizational actor.
 * Proposed by the ATC agent; accepted by humans.
 *
 * - human-led:     Requires human judgment and execution throughout.
 * - ai-assisted:   Human leads; AI handles discrete subtasks under human direction.
 * - ai-executable: AI executes autonomously once authorized; human reviews output.
 * - hybrid:        Explicit handoff points between human and AI execution phases.
 */
export type ExecutionProfile = 'human-led' | 'ai-assisted' | 'ai-executable' | 'hybrid';

/**
 * The actual classification values proposed or approved.
 *
 * Stored as JSONB in item_classification_proposals.classification_fields.
 * This shape is extensible: future classifiable fields can be added here
 * without requiring a schema migration on the proposals table.
 */
export interface ClassificationFields {
  /** Item category (problem / opportunity / risk / solution / commitment / investment) */
  category: ItemLanguageType;
  /** Altitude level 1–5 */
  altitudeLevel: AltitudeLevel;
  /** How this work is expected to be executed in a human+AI org */
  executionProfile?: ExecutionProfile;
  /** Responses to the three scoping questions (from the ATC agent or manual entry) */
  questionResponses?: AltitudeQuestionResponse[];
  /** Rationale text (present when proposed by an agent) */
  rationale?: string;
  /** Confidence signal from the proposing agent */
  confidence?: 'high' | 'medium' | 'low';
}

// ── Proposal record ───────────────────────────────────────────────────────────

/**
 * A classification proposal — one record per determination event.
 * Proposals are append-only; each accept or override creates a new row.
 *
 * status values:
 * - active     — the current open proposal for this item
 * - accepted   — a human confirmed this proposal
 * - superseded — a newer proposal replaced this one before it was acted on
 * - rejected   — a human explicitly dismissed this proposal
 */
export interface ItemClassificationProposal {
  id: string;
  workspaceId: string;
  itemId: string;

  /** 'agent' = ATC or another automated agent; 'user' = human direct entry */
  proposedBy: ClassificationProposedBy;
  /** Identifies which agent proposed (e.g. 'atc'). Null when proposedBy = 'user'. */
  proposedByAgent?: string | null;
  /** The user who initiated this proposal. Null for agent proposals. */
  proposedByUserId?: string | null;

  /** The classification values that were proposed. */
  classificationFields: ClassificationFields;

  status: 'active' | 'accepted' | 'superseded' | 'overridden' | 'rejected';
  acceptedByUserId?: string | null;
  acceptedAt?: string | null;
  /** Which fields were actually applied when status = 'accepted' (null = both, legacy). */
  acceptedFields?: { category: boolean; altitude: boolean } | null;
  createdAt: string;
}

// ── Converter ─────────────────────────────────────────────────────────────────

export function rowToClassificationProposal(
  row: Record<string, unknown>,
): ItemClassificationProposal {
  const fields = (row.classification_fields as Record<string, unknown>) ?? {};
  return {
    id: row.id as string,
    workspaceId: row.workspace_id as string,
    itemId: row.item_id as string,
    proposedBy: row.proposed_by as ClassificationProposedBy,
    proposedByAgent: (row.proposed_by_agent as string) ?? null,
    proposedByUserId: (row.proposed_by_user_id as string) ?? null,
    classificationFields: {
      category: (fields.category as ItemLanguageType) ?? 'problem',
      altitudeLevel: (fields.altitude_level as AltitudeLevel) ?? 3,
      executionProfile: (fields.execution_profile as ExecutionProfile) ?? undefined,
      questionResponses: (fields.question_responses as AltitudeQuestionResponse[]) ?? [],
      rationale: (fields.rationale as string) ?? undefined,
      confidence: (fields.confidence as 'high' | 'medium' | 'low') ?? undefined,
    },
    status: row.status as ItemClassificationProposal['status'],
    acceptedByUserId: (row.accepted_by_user_id as string) ?? null,
    acceptedAt: (row.accepted_at as string) ?? null,
    acceptedFields: (() => {
      const af = row.accepted_fields as Record<string, boolean> | null;
      if (!af) return null;
      return { category: af.category ?? true, altitude: af.altitude ?? true };
    })(),
    createdAt: row.created_at as string,
  };
}

// ── Display helpers ───────────────────────────────────────────────────────────

export const CLASSIFICATION_STATUS_LABELS: Record<ClassificationStatus, string> = {
  empty: 'Not classified',
  proposed: 'Classification proposed',
  approved: 'Classified',
};

export const CLASSIFICATION_STATUS_DESCRIPTIONS: Record<ClassificationStatus, string> = {
  empty: 'No category or altitude has been assigned to this item yet.',
  proposed: 'An agent has suggested a category and altitude — review and confirm or change.',
  approved: 'Category and altitude have been confirmed by a human.',
};

/** Human-readable proposal lifecycle status labels */
export const PROPOSAL_STATUS_LABELS: Record<
  'active' | 'accepted' | 'superseded' | 'overridden' | 'rejected',
  string
> = {
  active: 'Pending',
  accepted: 'Accepted',
  superseded: 'Superseded',
  overridden: 'Overridden',
  rejected: 'Dismissed',
};

/** Human-readable proposer label */
export function proposerLabel(proposal: ItemClassificationProposal): string {
  if (proposal.proposedBy === 'agent') {
    const agent = proposal.proposedByAgent ?? 'agent';
    return agent === 'atc' ? 'Air Traffic Controller' : agent;
  }
  return 'Set manually';
}
